import { Language } from './enums.js';
/**
 * The three pieces of the assistant's opening line, per language.
 *
 * They live here, in shared, because BOTH sides produce them and they drifted
 * the moment they did not. The portal seeded a new business's greeting field
 * with a hardcoded English sentence; the gateway had its own translated
 * default that only applied when that field was empty — which, after the
 * wizard, it never was. A Polish car wash therefore answered "Thank you for
 * calling Myjnia samochodowa. How can I help you today?", in English, followed
 * by a Polish AI disclosure in the same breath.
 *
 * One table, two callers: the wizard writes the owner's language into the
 * field, and the gateway falls back to the same wording if it is ever empty.
 */
/** Opener suggested to the owner, and used when they leave the field blank. */
export function defaultGreeting(businessName, language) {
    switch (language) {
        // Wording kept exactly as the gateway already had it — these are reviewed,
        // spoken-aloud lines, and this change is about WHERE they are used, not
        // about rewriting them.
        case 'pl':
            return `Dziękujemy za telefon do ${businessName}!`;
        case 'fr':
            return `Merci d'appeler ${businessName} !`;
        case 'de':
            return `Danke, dass Sie ${businessName} anrufen!`;
        default:
            return `Thanks for calling ${businessName}!`;
    }
}
/**
 * "How can I help?" — the question that follows the greeting and the AI
 * disclosure, with the caller's name when we know it.
 *
 * Was hardcoded English in the prompt, so every call in every language opened
 * with an English question no matter what came before it.
 */
export function askHowCanIHelp(language, clientName) {
    const name = clientName?.trim();
    switch (language) {
        case 'pl':
            return name ? `W czym mogę pomóc, ${name}?` : 'W czym mogę pomóc?';
        case 'fr':
            return name ? `Que puis-je faire pour vous, ${name} ?` : 'Que puis-je faire pour vous ?';
        case 'de':
            return name ? `Wie kann ich Ihnen helfen, ${name}?` : 'Wie kann ich Ihnen helfen?';
        default:
            return name ? `How can I help, ${name}?` : 'How can I help?';
    }
}
/**
 * The short opening a phone line answers with (doc 53 B6): the business, a
 * hello — no "thank you for calling", which made the opening ~10 s with the
 * disclosure. Used only where the gateway speaks the greeting itself.
 */
export function shortGreeting(businessName, language) {
    switch (language) {
        case 'pl':
            return `${businessName}, dzień dobry.`;
        case 'fr':
            return `${businessName}, bonjour.`;
        case 'de':
            return `${businessName}, guten Tag.`;
        default:
            return `${businessName}, hello.`;
    }
}
/**
 * The same disclosure as `aiDisclosure`, in the fewest words that still say
 * plainly that the caller is talking to an AI (doc 53 B6). Not optional and
 * not configurable either.
 */
export function aiDisclosureShort(language) {
    switch (language) {
        case 'pl':
            return 'Mówi asystent AI.';
        case 'fr':
            return "Ici l'assistant IA.";
        case 'de':
            return 'Hier spricht der KI-Assistent.';
        default:
            return "You're speaking with an AI assistant.";
    }
}
/**
 * Mandatory AI-assistant disclosure, appended to the greeting. Not optional and
 * not configurable: a caller is entitled to know within the first breath that
 * they are talking to a machine.
 */
export function aiDisclosure(language) {
    switch (language) {
        case 'pl':
            return 'Rozmawiasz z asystentem głosowym opartym na sztucznej inteligencji.';
        case 'fr':
            return "Vous parlez avec un assistant vocal à intelligence artificielle.";
        case 'de':
            return 'Sie sprechen mit einem KI-Sprachassistenten.';
        default:
            return "You're speaking with an AI assistant.";
    }
}
/** Whitespace-insensitive comparison, the same way the owner would read it. */
function sameText(a, b) {
    return a.replace(/\s+/gu, ' ').trim() === b.replace(/\s+/gu, ' ').trim();
}
/**
 * The wizard's suggestion before df03888 — English for every business. Still
 * stored on businesses that finished setup before that change.
 */
function legacyDefaultGreeting(businessName) {
    return `Thank you for calling ${businessName}. How can I help you today?`;
}
/**
 * Is `greeting` text WE generated for `businessName` (doc 68)?
 *
 * True for our suggestion in any language — Settings could change the language
 * without touching the greeting, so a Polish business may still hold the
 * English default — and for the legacy English suggestion. Anything else is the
 * owner's own wording, including a default they edited by one character.
 * An empty greeting is not "generated": the gateway already renders that from
 * the current name, so there is nothing to carry along.
 */
export function isGeneratedGreeting(greeting, businessName) {
    const name = businessName.trim();
    if (!greeting.trim() || !name)
        return false;
    if (sameText(greeting, legacyDefaultGreeting(name)))
        return true;
    return Language.options.some((lang) => sameText(greeting, defaultGreeting(name, lang)));
}
/** Is this business's greeting OUR sentence (rendered from its current name)? */
export function usesDefaultGreeting(b) {
    if (b.greetingMode === 'default')
        return true;
    if (b.greetingMode === 'custom')
        return false;
    return !b.greeting.trim() || isGeneratedGreeting(b.greeting, b.name);
}
/**
 * The opening callers hear, before the AI disclosure (doc 71 W1). Empty means
 * "no text of the owner's": each speaker keeps its own fallback for that.
 * Used by the publisher (so the published snapshot already says the current
 * name), the gateway and the portal preview — one rule for all of them.
 */
export function effectiveGreeting(b) {
    if (b.greetingMode === 'custom')
        return b.greeting.trim();
    // A legacy business with no text keeps each speaker's own fallback (Live's
    // short "Name, dzień dobry.", doc 53 B6) — exactly as before modes existed.
    if (b.greetingMode !== 'default' && !b.greeting.trim())
        return '';
    if (usesDefaultGreeting(b))
        return b.name.trim() ? defaultGreeting(b.name.trim(), b.language) : '';
    return b.greeting.trim();
}
/** Our sentence per language (and the legacy English one), split around the name. */
const TEMPLATES = (() => {
    const marker = '\u0000';
    const flat = (t) => t.replace(/\s+/gu, ' ').trim();
    return [...Language.options.map((l) => defaultGreeting(marker, l)), legacyDefaultGreeting(marker)].map((t) => {
        const [prefix = '', suffix = ''] = flat(t).split(marker);
        return { prefix, suffix };
    });
})();
/**
 * The business name inside a greeting that is one of OUR sentences — for ANY
 * name — or null (doc 71 W1). Bounded: the whole text must be exactly the
 * template around a name of at most 120 characters. Lets a screen say "your
 * greeting still says «Old name»" after any number of renames, where comparing
 * with the immediately previous name could not.
 */
export function generatedGreetingName(greeting) {
    const text = greeting.replace(/\s+/gu, ' ').trim();
    for (const { prefix, suffix } of TEMPLATES) {
        if (!text.startsWith(prefix) || !text.endsWith(suffix) || text.length <= prefix.length + suffix.length)
            continue;
        const name = text.slice(prefix.length, text.length - suffix.length).trim();
        // Our sentence with more after it ("…do Salonu Ani! Zapraszamy od 9.") is
        // the owner's own text, not a name: a name ends no sentence.
        // An initial ("A. Nowak Studio") is part of a name; "Salon. Zapraszamy" is not.
        if (/[!?]|\p{L}{2,}\.\s+\p{Lu}/u.test(name))
            continue;
        if (name && name.length <= 120)
            return name;
    }
    return null;
}
function sameName(a, b) {
    return a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
}
/** What Settings tells the owner about the greeting (doc 71 W1). */
export function greetingStatus(b) {
    if (usesDefaultGreeting(b))
        return { kind: 'default' };
    const named = generatedGreetingName(b.greeting);
    return named && !sameName(named, b.name) ? { kind: 'stale', staleName: named } : { kind: 'custom' };
}
/**
 * The mode a business stored before modes existed should get (doc 71 W1
 * migration): `default` only for empty text or OUR sentence for its current
 * name or a name it is VERIFIED to have had (its published configuration
 * history) — never a guess from the shape of the text alone. Everything else
 * is the owner's own wording and stays exactly as written.
 */
export function legacyGreetingMode(b, verifiedNames) {
    if (b.greetingMode)
        return b.greetingMode;
    if (!b.greeting.trim())
        return 'default';
    return [b.name, ...verifiedNames].some((n) => n.trim() && isGeneratedGreeting(b.greeting, n)) ? 'default' : 'custom';
}
/**
 * The greeting a business should store after it is renamed (doc 68 §1, doc 71).
 *
 * Both rename entry points — the setup wizard's profile step and Settings ›
 * Business — use this, and write the result in the SAME update as the name.
 * A default greeting is re-rendered from the new name in the business's
 * language (and marked `default`, so it keeps following the name); owner-written
 * text is never rewritten (a blanket find-and-replace of a name inside prose is
 * a guess), only reported — including our sentence left over from an OLDER name.
 */
export function greetingAfterRename(current, newName) {
    if (usesDefaultGreeting(current)) {
        return { kind: 'regenerated', greeting: defaultGreeting(newName.trim(), current.language) };
    }
    const oldName = current.name.trim();
    const mentionsOldName = oldName.length > 0 &&
        !sameName(oldName, newName) &&
        current.greeting.toLocaleLowerCase().includes(oldName.toLocaleLowerCase());
    const named = generatedGreetingName(current.greeting);
    return { kind: 'custom', mentionsOldName, staleName: named && !sameName(named, newName) ? named : null };
}
/**
 * The greeting after the assistant's language changes: generated text follows
 * the language, the owner's own words stay as written (same rule as the
 * wizard's language picker).
 */
export function greetingAfterLanguageChange(greeting, businessName, language) {
    return isGeneratedGreeting(greeting, businessName) ? defaultGreeting(businessName.trim(), language) : greeting;
}
//# sourceMappingURL=assistantCopy.js.map